import { createElement, Fragment, type ReactNode } from "react";
import { DEFAULT_LANG, interpolate, isLang, LANGS, LOCALE, lookup, makeTranslator, type Lang, type Vars } from "@shared/i18n";
import { DICT } from "./dict";

/**
 * Переводы интерфейса. Язык выбирается один раз при загрузке страницы (смена языка
 * перезагружает страницу), поэтому t() — обычная функция: работает и в компонентах,
 * и в константах модулей, и в тостах.
 *
 *   t("Новое место")                          → «Nauja vieta»
 *   t("Добавлено: {name}", { name })          → подстановка
 *   tn(n, "{n} место|{n} места|{n} мест")     → множественное число (3 русские формы)
 *   tj("Нажмите {btn}", { btn: <b>…</b> })    → то же с React-элементами
 */

const STORE_KEY = "tb:lang";

function detect(): Lang {
  try {
    const saved = localStorage.getItem(STORE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* приватный режим */
  }
  return DEFAULT_LANG;
}

export const lang: Lang = detect();
export const locale = LOCALE[lang];
export { LANGS };
export type { Lang };

if (typeof document !== "undefined") document.documentElement.lang = lang;

const { tr, trn } = makeTranslator(DICT);

export const t = (key: string, vars?: Vars) => tr(lang, key, vars);
export const tn = (n: number, key: string, vars?: Vars) => trn(lang, n, key, vars);
/** Пометка «это ключ перевода» для строк, которые переводятся позже через t(переменная) */
export const k = (key: string) => key;

export function tj(key: string, vars: Record<string, ReactNode>): ReactNode {
  const text = lookup(DICT, lang, key);
  const parts = text.split(/(\{\w+\})/);
  return createElement(
    Fragment,
    null,
    ...parts.map((p, i) => {
      const m = /^\{(\w+)\}$/.exec(p);
      if (m && m[1] in vars) return createElement(Fragment, { key: i }, vars[m[1]]);
      return interpolate(p);
    }),
  );
}

/** Сменить язык: запомнить, сообщить серверу (для ИИ и фоновых разборов) и перезагрузить страницу */
export async function setLang(next: Lang, notify?: (l: Lang) => Promise<unknown>) {
  if (next === lang) return;
  try {
    localStorage.setItem(STORE_KEY, next);
  } catch {
    /* ignore */
  }
  try {
    await notify?.(next);
  } catch {
    /* сервер узнает из заголовка X-Lang при следующем запросе */
  }
  location.reload();
}
